import React, { useState, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight, Send, CheckSquare, Square, Loader, AlertCircle, Bell, X } from 'lucide-react';

const BASE_URL = 'https://api.growl.games/reports';
const NOTIFICATION_URL = 'https://api.growl.games/notifications/admin';

interface User {
  id: string;
  name: string;
  avatar?: string;
  wager: string | number;
  games_played: number;
  vipDetails?: {
    vipLevel: string;
    previousVipLevel: string;
  };
}

interface ApiResponse {
  users: User[];
  pagination: {
    pages: number;
    current: number;
    total: number;
  };
}

interface NotificationForm {
  title: string;
  body: string;
  type: string;
  additional_data?: string;
}

const AdminNotificationSystem: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [showNotificationModal, setShowNotificationModal] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [notificationForm, setNotificationForm] = useState<NotificationForm>({
    title: '',
    body: '',
    type: 'info',
    additional_data: ''
  });

  // Toast notification states
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'warning'>('success');

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm]);

  useEffect(() => {
    if (selectAll && users.length > 0) {
      setSelectedUsers(new Set(users.map(u => u.id)));
    } else if (!selectAll) {
      setSelectedUsers(new Set());
    }
  }, [selectAll, users]);

  // Handle toast messages
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const showToastNotification = (message: string, type: 'success' | 'error' | 'warning') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const getAuthHeaders = () => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchUsers = async (): Promise<void> => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        search: searchTerm
      });

      const response = await fetch(`${BASE_URL}/users?${params}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) throw new Error('Failed to fetch users');

      const data: ApiResponse = await response.json();
      setUsers(data.users);
      setTotalPages(data.pagination.pages);
    } catch (error) {
      console.error('Error fetching users:', error);
      showToastNotification('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number): void => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const toggleUserSelection = (userId: string): void => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
    setSelectAll(false);
  };

  const toggleSelectAll = (): void => {
    setSelectAll(!selectAll);
  };

  const handleNotificationFormChange = (field: keyof NotificationForm, value: string): void => {
    setNotificationForm(prev => ({ ...prev, [field]: value }));
  };

  const sendNotifications = async (): Promise<void> => {
    if (selectedUsers.size === 0) {
      showToastNotification('Please select at least one user', 'warning');
      return;
    }

    if (!notificationForm.title || !notificationForm.body) {
      showToastNotification('Please fill in title and body', 'warning');
      return;
    }

    setSending(true);
    try {
      const userIds = Array.from(selectedUsers);

      const promises = userIds.map(userId =>
        fetch(`${NOTIFICATION_URL}/send`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            user_id: userId,
            title: notificationForm.title,
            body: notificationForm.body,
            type: notificationForm.type,
            is_read: false,
            additional_data: notificationForm.additional_data
              ? JSON.stringify({ extraInfo: notificationForm.additional_data })
              : JSON.stringify({ extraInfo: "" })
          })
        })
      );

      await Promise.all(promises);

      showToastNotification(`Successfully sent notifications to ${userIds.length} user(s)`, 'success');
      setShowNotificationModal(false);
      setNotificationForm({
        title: '',
        body: '',
        type: 'info',
        additional_data: ''
      });
      setSelectedUsers(new Set());
      setSelectAll(false);
    } catch (error) {
      console.error('Error sending notifications:', error);
      showToastNotification('Failed to send notifications. Please try again.', 'error');
    } finally {
      setSending(false);
    }
  };

  const openNotificationModal = (): void => {
    if (selectedUsers.size === 0) {
      showToastNotification('Please select at least one user', 'warning');
      return;
    }
    setShowNotificationModal(true);
  };

  return (
    <div className="ml-0 lg:ml-[256px] p-2">
      {/* Toast Notification */}
      {showToast && (
        <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-[9999] ${
          toastType === 'success' ? 'bg-green-500' :
          toastType === 'error' ? 'bg-red-500' :
          'bg-yellow-500'
        } text-white animate-slide-in`}>
          <div className="flex items-center gap-2">
            {toastType === 'success' && (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            )}
            {toastType === 'error' && (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            )}
            {toastType === 'warning' && (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            )}
            <span>{toastMessage}</span>
          </div>
        </div>
      )}

      <div style={{
        padding: '24px',
        backgroundColor: '#232626',
        color: '#fff',
        minHeight: '100vh'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <h1 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#ffffff'
          }}>User Notification System</h1>
          <button
            onClick={openNotificationModal}
            disabled={selectedUsers.size === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: selectedUsers.size === 0 ? '#4A4D56' : '#5D81AC',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 18px',
              cursor: selectedUsers.size === 0 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              transition: 'background-color 0.2s',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => {
              if (selectedUsers.size > 0) {
                (e.target as HTMLButtonElement).style.backgroundColor = '#4A6889';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedUsers.size > 0) {
                (e.target as HTMLButtonElement).style.backgroundColor = '#5D81AC';
              }
            }}
          >
            <Bell size={18} />
            Send Notification ({selectedUsers.size})
          </button>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#2A2D36',
          borderRadius: '8px',
          padding: '0 12px',
          marginBottom: '24px',
          border: '1px solid #3A3D46'
        }}>
          <div style={{ marginRight: '8px' }}>
            <Search size={16} color="#8A8D93" />
          </div>
          <input
            placeholder="Search by username..."
            value={searchTerm}
            onChange={handleSearchChange}
            style={{
              flex: 1,
              background: 'none',
              border: 'none',
              color: '#fff',
              padding: '12px 0',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>

        {loading ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '300px',
            gap: '16px'
          }}>
            <Loader size={32} color="#5D81AC" className="animate-spin" />
            <p style={{ fontSize: '16px', color: '#8A8D93' }}>Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '300px',
            fontSize: '16px',
            color: '#8A8D93',
            gap: '16px'
          }}>
            <AlertCircle size={48} color="#5D81AC" />
            <p>No users found</p>
          </div>
        ) : (
          <>
            <div style={{
              overflowX: 'auto',
              backgroundColor: '#2A2D36',
              borderRadius: '8px',
              marginBottom: '24px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse'
              }}>
                <thead style={{ backgroundColor: '#323738' }}>
                  <tr>
                    <th style={{
                      textAlign: 'left',
                      padding: '16px',
                      fontWeight: '500',
                      color: '#8A8D93',
                      fontSize: '14px',
                      width: '50px'
                    }}>
                      <button
                        onClick={toggleSelectAll}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          color: '#8A8D93',
                          padding: 0
                        }}
                      >
                        {selectAll ? <CheckSquare size={20} color="#5D81AC" /> : <Square size={20} />}
                      </button>
                    </th>
                    <th style={{
                      textAlign: 'left',
                      padding: '16px',
                      fontWeight: '500',
                      color: '#8A8D93',
                      fontSize: '14px'
                    }}>User</th>
                    <th style={{
                      textAlign: 'left',
                      padding: '16px',
                      fontWeight: '500',
                      color: '#8A8D93',
                      fontSize: '14px'
                    }}>User ID</th>
                    <th style={{
                      textAlign: 'left',
                      padding: '16px',
                      fontWeight: '500',
                      color: '#8A8D93',
                      fontSize: '14px'
                    }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr
                      key={user.id}
                      style={{
                        borderBottom: '1px solid #3A3D46',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#323738'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <td style={{ padding: '16px', fontSize: '14px' }}>
                        <button
                          onClick={() => toggleUserSelection(user.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            padding: 0
                          }}
                        >
                          {selectedUsers.has(user.id) ? (
                            <CheckSquare size={20} color="#5D81AC" />
                          ) : (
                            <Square size={20} color="#8A8D93" />
                          )}
                        </button>
                      </td>
                      <td style={{ padding: '16px', fontSize: '14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontWeight: '500' }}>{user.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '16px', fontSize: '14px', color: '#8A8D93', fontFamily: 'monospace' }}>
                        {String(user.id).slice(0, 12)}
                      </td>
                      <td style={{ padding: '16px', fontSize: '14px' }}>
                        <button
                          onClick={() => {
                            setSelectedUsers(new Set([user.id]));
                            setShowNotificationModal(true);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: '#5D81AC',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            transition: 'background-color 0.2s',
                            fontWeight: '500'
                          }}
                          onMouseEnter={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#4A6889'}
                          onMouseLeave={(e) => (e.target as HTMLButtonElement).style.backgroundColor = '#5D81AC'}
                        >
                          <Send size={14} />
                          Send Notification
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 0',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  backgroundColor: '#323738',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: currentPage === 1 ? 0.5 : 1,
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== 1) {
                    (e.target as HTMLButtonElement).style.backgroundColor = '#3A3D46';
                  }
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = '#323738';
                }}
              >
                <ChevronLeft size={16} />
                Previous
              </button>
              <div style={{ fontSize: '14px', color: '#8A8D93' }}>
                Page {currentPage} of {totalPages}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  backgroundColor: '#323738',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: currentPage === totalPages ? 0.5 : 1,
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== totalPages) {
                    (e.target as HTMLButtonElement).style.backgroundColor = '#3A3D46';
                  }
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = '#323738';
                }}
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          </>
        )}

        {showNotificationModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <div
              onClick={() => setShowNotificationModal(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(4px)',
                zIndex: 1000
              }}
            />
            <div style={{
              position: 'relative',
              backgroundColor: '#2A2D36',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '600px',
              color: 'white',
              maxHeight: '80vh',
              overflowY: 'auto',
              zIndex: 1001,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              padding: '24px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px'
              }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>
                  Send Notification to {selectedUsers.size} User(s)
                </h2>
                <button
                  onClick={() => setShowNotificationModal(false)}
                  style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => (e.target as HTMLButtonElement).style.background = 'rgba(0, 0, 0, 0.5)'}
                  onMouseLeave={(e) => (e.target as HTMLButtonElement).style.background = 'rgba(0, 0, 0, 0.3)'}
                >
                  <X size={20} />
                </button>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  color: '#8A8D93',
                  fontWeight: '500'
                }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={notificationForm.title}
                  onChange={(e) => handleNotificationFormChange('title', e.target.value)}
                  placeholder="Enter notification title"
                  style={{
                    width: '100%',
                    backgroundColor: '#232626',
                    border: '1px solid #3A3D46',
                    borderRadius: '6px',
                    padding: '12px',
                    color: '#fff',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  color: '#8A8D93',
                  fontWeight: '500'
                }}>
                  Body *
                </label>
                <textarea
                  value={notificationForm.body}
                  onChange={(e) => handleNotificationFormChange('body', e.target.value)}
                  placeholder="Enter notification message"
                  rows={4}
                  style={{
                    width: '100%',
                    backgroundColor: '#232626',
                    border: '1px solid #3A3D46',
                    borderRadius: '6px',
                    padding: '12px',
                    color: '#fff',
                    fontSize: '14px',
                    outline: 'none',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  color: '#8A8D93',
                  fontWeight: '500'
                }}>
                  Type
                </label>
                <select
                  value={notificationForm.type}
                  onChange={(e) => handleNotificationFormChange('type', e.target.value)}
                  style={{
                    width: '100%',
                    backgroundColor: '#232626',
                    border: '1px solid #3A3D46',
                    borderRadius: '6px',
                    padding: '12px',
                    color: '#fff',
                    fontSize: '14px',
                    outline: 'none',
                    cursor: 'pointer',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="info">Info</option>
                  <option value="success">Success</option>
                  <option value="warning">Warning</option>
                  <option value="Redeemed">Redeemed</option>
                </select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  color: '#8A8D93',
                  fontWeight: '500'
                }}>
                  Additional Data (Optional)
                </label>
                <input
                  type="text"
                  value={notificationForm.additional_data}
                  onChange={(e) => handleNotificationFormChange('additional_data', e.target.value)}
                  placeholder="Additional information"
                  style={{
                    width: '100%',
                    backgroundColor: '#232626',
                    border: '1px solid #3A3D46',
                    borderRadius: '6px',
                    padding: '12px',
                    color: '#fff',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => setShowNotificationModal(false)}
                  disabled={sending}
                  style={{
                    backgroundColor: '#3A3D46',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    cursor: sending ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: sending ? 0.5 : 1,
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!sending) {
                      (e.target as HTMLButtonElement).style.backgroundColor = '#4A4D56';
                    }
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLButtonElement).style.backgroundColor = '#3A3D46';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={sendNotifications}
                  disabled={sending || !notificationForm.title || !notificationForm.body}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: '#5D81AC',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    cursor: (sending || !notificationForm.title || !notificationForm.body) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: (sending || !notificationForm.title || !notificationForm.body) ? 0.5 : 1,
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!sending && notificationForm.title && notificationForm.body) {
                      (e.target as HTMLButtonElement).style.backgroundColor = '#4A6889';
                    }
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLButtonElement).style.backgroundColor = '#5D81AC';
                  }}
                >
                  {sending ? (
                    <>
                      <Loader size={16} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Send Notification
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNotificationSystem;